import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import SearchableSelect from '../components/common/SearchableSelect';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { TableColumn } from '../components/common/MobileCardList';
import { api } from '../services/api';
import { uomService, Uom } from '../services/uomService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { downloadStyledProductExport } from '../utils/reportExcel';
import { INPUT_SANITIZERS } from '../utils/validationHelpers';

// Digits + at most one decimal point, for weight-style fields.
const sanitizeDecimalQty = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Client {
  id: number;
  companyName: string;
  warehouseId: number | null;
}

interface Warehouse {
  id: number;
  name: string;
}

interface Product {
  id: number;
  uuid: string;
  productDescription: string;
  itemCode: string | null;
  unitType: string | null;
  weightPerUnit: string | null;
  warehouseId: number | null;
  clientId: number | null;
  packSize: string | null;
  boxWeight: string | null;
  grossWeight: string | null;
  masterPacking: number | null;
  createdBy: number | null;
  updatedBy: number | null;
  clientName: string | null;
  warehouseName: string | null;
  tenantId: number;
  createdAt: string;
  updatedAt: string;
}

interface Batch {
  id: number;
  productMasterId: number;
  batchNo: string;
  skuCode: string | null;
  customerSapCode: string | null;
  mfgDate: string | null;
  expiryDate: string | null;
  qtyPerPallet: number | null;
  lotNo: string | null;
  productMaster?: Product;
}

interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
}

const EMPTY_PRODUCT = { productDescription: '', itemCode: '', unitType: '', warehouseId: '', clientId: '', packSize: '', grossWeightPerUnit: '', masterPacking: '' };
const EMPTY_BATCH = { productMasterId: 0, batchNo: '', skuCode: '', customerSapCode: '', mfgDate: '', expiryDate: '', qtyPerPallet: '', lotNo: '' };
const EMPTY_UOM = { code: '', description: '' };

// ─── Import result modal ──────────────────────────────────────────────────────

function ImportResultModal({ result, onClose }: { result: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Import Result</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4 border-b grid grid-cols-3 gap-3 text-center">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-700">{result.totalRows}</div>
            <div className="text-xs text-gray-500 mt-1">Total Rows</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-green-600">{result.succeeded}</div>
            <div className="text-xs text-gray-500 mt-1">Succeeded</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-red-600">{result.failed}</div>
            <div className="text-xs text-gray-500 mt-1">Failed</div>
          </div>
        </div>
        {result.results?.filter((r: any) => r.status !== 'success').length > 0 && (
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Failed / Skipped Rows</p>
            {result.results.filter((r: any) => r.status !== 'success').map((r: any) => (
              <div key={r.row} className="text-xs bg-red-50 text-red-700 rounded px-3 py-1.5 mb-1">
                Row {r.row}: {r.reason ?? r.status}
              </div>
            ))}
          </div>
        )}
        <div className="p-4 border-t">
          <button onClick={onClose} className="w-full py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProductCatalogPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('master-data:manage');
  const canImport = hasPermission('master-data:import');
  const canExport = hasPermission('master-data:export');
  const { confirm, ConfirmDialog } = useConfirm();
  const toast = useToast();

  const [tab, setTab] = useState<'products' | 'batches' | 'uom'>('products');

  // Product state
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productWarehouseFilter, setProductWarehouseFilter] = useState<string>('');
  const [productClientFilter, setProductClientFilter] = useState<string>('');
  const [batchClientFilter, setBatchClientFilter] = useState<string>('');
  const [productModal, setProductModal] = useState<{ open: boolean; editing: Product | null }>({ open: false, editing: null });
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);

  // Batch state
  const [batchSearch, setBatchSearch] = useState('');
  const [batchPage, setBatchPage] = useState(1);
  const [batchProductFilter, setBatchProductFilter] = useState<number | undefined>(undefined);
  const [batchModal, setBatchModal] = useState<{ open: boolean; editing: Batch | null }>({ open: false, editing: null });
  const [batchForm, setBatchForm] = useState(EMPTY_BATCH);

  // UOM state
  const [uomSearch, setUomSearch] = useState('');
  const [uomPage, setUomPage] = useState(1);
  const [uomModal, setUomModal] = useState<{ open: boolean; editing: Uom | null }>({ open: false, editing: null });
  const [uomForm, setUomForm] = useState(EMPTY_UOM);

  // Import state
  const [importResult, setImportResult] = useState<any>(null);
  const combinedFileRef = useRef<HTMLInputElement>(null);
  const uomFileRef = useRef<HTMLInputElement>(null);
  const batchFileRef = useRef<HTMLInputElement>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const warehousesQuery = useQuery({
    queryKey: ['warehouses-list'],
    queryFn: async () => {
      const res = await api.get('/warehouses?page=1&limit=100');
      return ((res as any)?.data?.items ?? []) as Warehouse[];
    },
    staleTime: 120_000,
  });

  const toolbarClientsQuery = useQuery({
    queryKey: ['clients-by-warehouse', productWarehouseFilter],
    queryFn: async () => {
      const res = await api.get(`/clients?warehouseId=${productWarehouseFilter}&limit=100&sort=companyName&order=ASC`);
      return ((res as any)?.data?.items ?? []) as Client[];
    },
    enabled: !!productWarehouseFilter,
    staleTime: 60_000,
  });

  const modalClientsQuery = useQuery({
    queryKey: ['clients-by-warehouse', productForm.warehouseId],
    queryFn: async () => {
      const res = await api.get(`/clients?warehouseId=${productForm.warehouseId}&limit=100&sort=companyName&order=ASC`);
      return ((res as any)?.data?.items ?? []) as Client[];
    },
    enabled: !!productForm.warehouseId,
    staleTime: 60_000,
  });

  const productsQuery = useQuery({
    queryKey: ['master-products', productPage, productSearch, productWarehouseFilter, productClientFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(productPage), limit: '20' });
      if (productSearch) p.set('search', productSearch);
      if (productWarehouseFilter) p.set('warehouseId', productWarehouseFilter);
      if (productClientFilter) p.set('clientId', productClientFilter);
      const res = await api.get(`/master-data/products?${p}`);
      return (res as any)?.data as PagedResult<Product>;
    },
  });

  const batchesQuery = useQuery({
    queryKey: ['master-batches', batchPage, batchSearch, batchProductFilter, batchClientFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(batchPage), limit: '20' });
      if (batchSearch) p.set('search', batchSearch);
      if (batchProductFilter) p.set('productMasterId', String(batchProductFilter));
      if (batchClientFilter) p.set('clientId', batchClientFilter);
      const res = await api.get(`/master-data/batches?${p}`);
      return (res as any)?.data as PagedResult<Batch>;
    },
  });

  const uomsQuery = useQuery({
    queryKey: ['uoms', uomPage, uomSearch],
    queryFn: () => uomService.list({ page: uomPage, limit: 20, search: uomSearch || undefined }),
  });

  // All UOMs (unpaginated) — used to back the unit dropdowns wherever a unit is selected
  const allUomsQuery = useQuery({
    queryKey: ['uoms-all'],
    queryFn: () => uomService.list({ page: 1, limit: 500 }),
    staleTime: 60_000,
  });

  // All products for batch dropdown (effectively unbounded), scoped to the selected batch client filter
  const allProductsQuery = useQuery({
    queryKey: ['master-products-all', batchClientFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: '1', limit: '5000' });
      if (batchClientFilter) p.set('clientId', batchClientFilter);
      const res = await api.get(`/master-data/products?${p}`);
      return ((res as any)?.data?.items ?? []) as Product[];
    },
    staleTime: 60_000,
  });

  // ─── Product mutations ────────────────────────────────────────────────────

  const saveProductMutation = useMutation({
    mutationFn: async (form: typeof EMPTY_PRODUCT) => {
      const body = {
        productDescription: form.productDescription,
        itemCode: form.itemCode || null,
        unitType: form.unitType || null,
        // Total Quantity Per Unit — auto-calculated, not a manually entered value.
        weightPerUnit: (form.packSize && form.masterPacking)
          ? parseFloat(form.packSize) * parseFloat(form.masterPacking)
          : null,
        warehouseId: form.warehouseId ? Number(form.warehouseId) : null,
        clientId: form.clientId ? Number(form.clientId) : null,
        packSize: form.packSize ? String(parseFloat(form.packSize)) : null,
        boxWeight: (form.packSize && form.masterPacking)
          ? parseFloat(form.packSize) * parseFloat(form.masterPacking)
          : null,
        grossWeight: form.grossWeightPerUnit ? parseFloat(form.grossWeightPerUnit) : null,
        masterPacking: form.masterPacking ? parseInt(form.masterPacking, 10) : null,
      };
      if (productModal.editing) {
        return api.put(`/master-data/products/${productModal.editing.id}`, body);
      }
      return api.post('/master-data/products', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-products'] });
      qc.invalidateQueries({ queryKey: ['master-products-all'] });
      setProductModal({ open: false, editing: null });
      setProductForm(EMPTY_PRODUCT);
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/master-data/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['master-products'] }),
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete product. Please try again.';
      toast.error(message);
    },
  });

  // ─── Batch mutations ──────────────────────────────────────────────────────

  const saveBatchMutation = useMutation({
    mutationFn: async (form: typeof EMPTY_BATCH) => {
      const body = {
        productMasterId: form.productMasterId,
        batchNo: form.batchNo,
        skuCode: form.skuCode || null,
        customerSapCode: form.customerSapCode || null,
        mfgDate: form.mfgDate || null,
        expiryDate: form.expiryDate || null,
        qtyPerPallet: form.qtyPerPallet ? Number(form.qtyPerPallet) : null,
        lotNo: form.lotNo || null,
      };
      if (batchModal.editing) {
        return api.put(`/master-data/batches/${batchModal.editing.id}`, body);
      }
      return api.post('/master-data/batches', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['master-batches'] });
      setBatchModal({ open: false, editing: null });
      setBatchForm(EMPTY_BATCH);
    },
  });

  const deleteBatchMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/master-data/batches/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['master-batches'] }),
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete batch. Please try again.';
      toast.error(message);
    },
  });

  const importBatchesMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/master-data/batches/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return (res as any)?.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['master-batches'] });
      setImportResult(data);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Import failed. Please try again.';
      toast.error(message);
    },
  });

  const importCombinedMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/master-data/import-combined', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      return (res as any)?.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['master-products'] });
      qc.invalidateQueries({ queryKey: ['master-products-all'] });
      qc.invalidateQueries({ queryKey: ['master-batches'] });
      setImportResult(data);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Import failed. Please try again.';
      toast.error(message);
    },
  });

  // ─── UOM mutations ────────────────────────────────────────────────────────

  const saveUomMutation = useMutation({
    mutationFn: async (form: typeof EMPTY_UOM) => {
      const body = { code: form.code, description: form.description || null };
      if (uomModal.editing) {
        return uomService.update(uomModal.editing.id, body);
      }
      return uomService.create(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['uoms'] });
      qc.invalidateQueries({ queryKey: ['uoms-all'] });
      setUomModal({ open: false, editing: null });
      setUomForm(EMPTY_UOM);
    },
  });

  const deleteUomMutation = useMutation({
    mutationFn: (id: number) => uomService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['uoms'] });
      qc.invalidateQueries({ queryKey: ['uoms-all'] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete UOM. Please try again.';
      toast.error(message);
    },
  });

  const importUomMutation = useMutation({
    mutationFn: (file: File) => uomService.import(file),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['uoms'] });
      qc.invalidateQueries({ queryKey: ['uoms-all'] });
      setImportResult(data);
    },
  });

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openAddProduct = () => { setProductForm(EMPTY_PRODUCT); setProductModal({ open: true, editing: null }); };
  const openEditProduct = (p: Product) => {
    setProductForm({
      productDescription: p.productDescription,
      itemCode: p.itemCode ?? '',
      unitType: p.unitType ?? '',
      warehouseId: p.warehouseId != null ? String(p.warehouseId) : '',
      clientId: p.clientId != null ? String(p.clientId) : '',
      packSize: p.packSize ?? '',
      grossWeightPerUnit: p.grossWeight ?? '',
      masterPacking: p.masterPacking != null ? String(p.masterPacking) : '',
    });
    setProductModal({ open: true, editing: p });
  };

  const openAddBatch = () => { setBatchForm(EMPTY_BATCH); setBatchModal({ open: true, editing: null }); };
  const openEditBatch = (b: Batch) => {
    setBatchForm({ productMasterId: b.productMasterId, batchNo: b.batchNo, skuCode: b.skuCode ?? '', customerSapCode: b.customerSapCode ?? '', mfgDate: b.mfgDate ?? '', expiryDate: b.expiryDate ?? '', qtyPerPallet: b.qtyPerPallet?.toString() ?? '', lotNo: b.lotNo ?? '' });
    setBatchModal({ open: true, editing: b });
  };

  const openAddUom = () => { setUomForm(EMPTY_UOM); setUomModal({ open: true, editing: null }); };
  const openEditUom = (u: Uom) => {
    setUomForm({ code: u.code, description: u.description ?? '' });
    setUomModal({ open: true, editing: u });
  };

  // ─── Template download helpers ────────────────────────────────────────────

  const downloadTemplate = (type: 'products' | 'batches' | 'uom') => {
    const headers = type === 'products'
      ? ['Product Description', 'Item Code', 'Unit Type', 'Weight Per Unit (kg)', 'Pack Size (units)', 'Warehouse Name']
      : type === 'batches'
      ? ['Product Description', 'Batch No', 'SKU Code', 'Customer SAP Code', 'MFG Date (YYYY-MM-DD)', 'Expiry Date (YYYY-MM-DD)']
      : ['Code', 'Description'];
    const csv = headers.join(',') + '\n';
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `${type}-import-template.csv`;
    a.click();
  };

  // ─── Export ───────────────────────────────────────────────────────────────

  const [exportingProducts, setExportingProducts] = useState(false);

  const exportProducts = async () => {
    setExportingProducts(true);
    try {
      const p = new URLSearchParams();
      if (productWarehouseFilter) p.set('warehouseId', productWarehouseFilter);
      if (productClientFilter) p.set('clientId', productClientFilter);
      const res = await api.get(`/master-data/export-combined?${p}`);
      const items: any[] = (res as any)?.data ?? [];

      const headers = ['WH Name', 'Customer Name', 'Item Code', 'Description', 'Pack Size', 'Master Packing', 'UOM', 'Total Qty of UOM', 'Net Wt. of UOM', 'Gross Wt. of UOM', 'Lot No.', 'Batch No', 'MFG DATE', 'EXP DATE', 'Pallet Capacity'];
      const rows = items.map(item => {
        const packSize = Number(item.packSize) || 0;
        const masterPacking = Number(item.masterPacking) || 0;
        // Gate on presence, not truthiness: a computed 0 (e.g. masterPacking = 0) is a real value and must export as 0, not blank.
        const hasPackConfig = item.packSize !== '' && item.packSize != null
          && item.masterPacking !== '' && item.masterPacking != null;
        return [
          item.warehouseName ?? '',
          item.clientName ?? '',
          item.itemCode ?? '',
          item.productDescription ?? '',
          item.packSize ?? '',
          item.masterPacking ?? '',
          item.unitType ?? '',
          hasPackConfig ? packSize * masterPacking : '',
          item.weightPerUnit ?? '',
          item.grossWeight ?? '',
          item.lotNo ?? '',
          item.batchNo ?? '',
          item.mfgDate ?? '',
          item.expiryDate ?? '',
          item.qtyPerPallet ?? '',
        ];
      });

      const title = productClientFilter
        ? (items[0]?.clientName || 'Client')
        : 'All Clients';

      await downloadStyledProductExport({
        fileName: `products-export-${new Date().toISOString().slice(0, 10)}.xlsx`,
        title,
        headers,
        rows,
      });
    } finally {
      setExportingProducts(false);
    }
  };

  // ─── UOM export ─────────────────────────────────────────────────────────

  const [exportingUoms, setExportingUoms] = useState(false);

  const exportUoms = async () => {
    setExportingUoms(true);
    try {
      const items = await uomService.export();
      const headers = ['Code', 'Description', 'Created By', 'Updated By', 'Created At', 'Updated At'];
      const rows = items.map(u =>
        [
          u.code,
          u.description,
          u.createdBy != null ? `User #${u.createdBy}` : '',
          u.updatedBy != null ? `User #${u.updatedBy}` : '',
          u.createdAt,
          u.updatedAt,
        ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')
      );
      const csv = [headers.join(','), ...rows].join('\n');
      const a = document.createElement('a');
      a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      a.download = `uom-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } finally {
      setExportingUoms(false);
    }
  };

  // ─── Table columns (desktop + mobile cards) ───────────────────────────────

  const productColumns: TableColumn[] = [
    {
      key: 'warehouseName',
      label: 'Warehouse',
      render: (value) => (
        <span className="text-gray-600 whitespace-nowrap">{value ?? '—'}</span>
      ),
    },
    {
      key: 'clientName',
      label: 'Client',
      render: (value) => (
        <span className="text-gray-600 whitespace-nowrap text-xs max-w-[140px] truncate inline-block" title={value ?? ''}>
          {value ?? '—'}
        </span>
      ),
    },
    {
      key: 'productDescription',
      label: 'Product Description',
      render: (value) => (
        <span className="font-medium text-gray-900 max-w-xs truncate inline-block" title={value}>{value}</span>
      ),
    },
    {
      key: 'itemCode',
      label: 'Item Code',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'masterPacking',
      label: 'Master Packing',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'packSize',
      label: 'Pack Size',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'unitType',
      label: 'Unit Of Measurement',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'weightPerUnit',
      label: 'Total Quantity Per Unit',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'boxWeight',
      label: 'Net Weight',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'grossWeight',
      label: 'Gross Weight',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, p: Product) => (
        <div className="flex gap-2">
          <button
            onClick={() => { setBatchProductFilter(p.id); if (p.clientId) setBatchClientFilter(String(p.clientId)); setTab('batches'); }}
            className="text-xs px-2 py-1 border border-brand-200 text-brand-600 rounded hover:bg-brand-50"
          >Batches</button>
          {canManage && (
            <>
              <button onClick={() => openEditProduct(p)} className="text-xs px-2 py-1 border border-brand-200 text-brand-600 rounded hover:bg-brand-50">Edit</button>
              <button
                onClick={async () => { if (await confirm({ title: 'Delete Product', message: 'Delete this product?', confirmText: 'Delete', variant: 'danger' })) deleteProductMutation.mutate(p.id); }}
                className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
              >Delete</button>
            </>
          )}
        </div>
      ),
    },
  ];

  const batchColumns: TableColumn[] = [
    {
      key: 'productMaster',
      label: 'Product',
      render: (_value, b: Batch) => (
        <span className="text-gray-700 max-w-xs truncate text-xs inline-block" title={b.productMaster?.productDescription}>
          {b.productMaster?.productDescription ?? `#${b.productMasterId}`}
        </span>
      ),
    },
    {
      key: 'batchNo',
      label: 'Batch No',
      render: (value) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'lotNo',
      label: 'Lot No',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'skuCode',
      label: 'SKU Code',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'customerSapCode',
      label: 'Customer SAP Code',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'mfgDate',
      label: 'MFG Date',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'qtyPerPallet',
      label: 'Qty/Pallet',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, b: Batch) => (
        canManage ? (
          <div className="flex gap-2">
            <button onClick={() => openEditBatch(b)} className="text-xs px-2 py-1 border border-brand-200 text-brand-600 rounded hover:bg-brand-50">Edit</button>
            <button
              onClick={async () => { if (await confirm({ title: 'Delete Batch', message: 'Delete this batch?', confirmText: 'Delete', variant: 'danger' })) deleteBatchMutation.mutate(b.id); }}
              className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
            >Delete</button>
          </div>
        ) : null
      ),
    },
  ];

  const uomColumns: TableColumn[] = [
    {
      key: 'code',
      label: 'Code',
      render: (value) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => <span className="text-gray-600">{value ?? '—'}</span>,
    },
    {
      key: 'createdBy',
      label: 'Created By',
      render: (value) => <span className="text-gray-600">{value != null ? `User #${value}` : '—'}</span>,
    },
    {
      key: 'updatedBy',
      label: 'Updated By',
      render: (value) => <span className="text-gray-600">{value != null ? `User #${value}` : '—'}</span>,
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      render: (value) => (
        <span className="text-gray-600 whitespace-nowrap">{new Date(value).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated Date',
      render: (value) => (
        <span className="text-gray-600 whitespace-nowrap">{new Date(value).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_value, u: Uom) => (
        canManage ? (
          <div className="flex gap-2">
            <button onClick={() => openEditUom(u)} className="text-xs px-2 py-1 border border-brand-200 text-brand-600 rounded hover:bg-brand-50">Edit</button>
            <button
              onClick={async () => { if (await confirm({ title: 'Delete UOM', message: 'Delete this unit of measurement?', confirmText: 'Delete', variant: 'danger' })) deleteUomMutation.mutate(u.id); }}
              className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
            >Delete</button>
          </div>
        ) : null
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">

        {/* Header */}
        <div className="mb-6 flex items-center gap-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow border-2 border-brand-200">
          <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7M4 7c0-2 1-3 3-3h10c2 0 3 1 3 3M4 7h16M10 12h4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Catalog</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage product master records and batch configurations</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {(['products', 'batches', 'uom'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-brand-600 text-white shadow' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {t === 'products' ? '📦 Products' : t === 'batches' ? '🗂️ Batches' : '📏 UOM'}
            </button>
          ))}
        </div>

        {/* ── PRODUCTS TAB ── */}
        {tab === 'products' && (
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {/* Toolbar */}
            <div className="p-4 border-b flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-2 items-center flex-wrap w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search product description, item code..."
                    value={productSearch}
                    onChange={e => { setProductSearch(e.target.value); setProductPage(1); }}
                    className="border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                  {productSearch && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => { setProductSearch(''); setProductPage(1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >✕</button>
                  )}
                </div>
                <select
                  value={productWarehouseFilter}
                  onChange={e => { setProductWarehouseFilter(e.target.value); setProductClientFilter(''); setProductPage(1); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  <option value="">All Warehouses</option>
                  {(warehousesQuery.data ?? []).map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <select
                  value={productClientFilter}
                  onChange={e => { setProductClientFilter(e.target.value); setProductPage(1); }}
                  disabled={!productWarehouseFilter}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 max-w-[200px] disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <option value="">{productWarehouseFilter ? 'All Clients' : 'Select warehouse first'}</option>
                  {(toolbarClientsQuery.data ?? []).map(c => (
                    <option key={c.id} value={c.id}>{c.companyName}</option>
                  ))}
                </select>
                {productWarehouseFilter && !toolbarClientsQuery.isLoading && (toolbarClientsQuery.data ?? []).length === 0 && (
                  <span className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">
                    No clients for this warehouse
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {canImport && (
                  <label className="text-xs px-3 py-1.5 border border-orange-300 rounded-lg text-orange-700 hover:bg-orange-50 flex items-center gap-1 cursor-pointer">
                    {importCombinedMutation.isPending ? 'Importing…' : '↑ Import'}
                    <input ref={combinedFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        if (!f.name.toLowerCase().includes('productmaster')) {
                          toast.error('Invalid file. The file name must contain "ProductMaster" (e.g. ProductMaster.xlsx).');
                          return;
                        }
                        importCombinedMutation.mutate(f);
                      }} />
                  </label>
                )}
                {canExport && (
                  <button
                    onClick={exportProducts}
                    disabled={exportingProducts}
                    className="text-xs px-3 py-1.5 border border-brand-300 rounded-lg text-brand-700 hover:bg-brand-50 flex items-center gap-1 disabled:opacity-50"
                  >
                    {exportingProducts ? 'Exporting…' : '↓ Export'}
                  </button>
                )}
                {canManage && (
                  <button onClick={openAddProduct} className="text-xs px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">
                    + Add Product
                  </button>
                )}
              </div>
            </div>

            {/* Import hint */}
            <div className="px-4 py-2 bg-brand-50 border-b border-brand-100 text-xs text-brand-700">
              <strong>↑ Import / ↓ Export format:</strong>&nbsp;
              A = Warehouse Name&nbsp;·&nbsp;B = Client Name&nbsp;·&nbsp;C = Product Description&nbsp;·&nbsp;D = Item Code&nbsp;·&nbsp;E = Unit Type&nbsp;·&nbsp;F = Weight/Unit (Kg)&nbsp;·&nbsp;<strong>G = Gross Weight (kg)</strong>&nbsp;·&nbsp;H = Pack Size&nbsp;·&nbsp;I = Lot No&nbsp;·&nbsp;J = Batch No&nbsp;·&nbsp;K = SKU Code&nbsp;·&nbsp;L = Customer SAP Code&nbsp;·&nbsp;M = MFG Date&nbsp;·&nbsp;N = Expiry Date&nbsp;·&nbsp;O = Qty/Pallet&nbsp;·&nbsp;<strong>P = Master Packing</strong>
            </div>

            {/* Table / mobile cards */}
            <div className="px-1 py-3 lg:p-0">
              <ResponsiveTable
                columns={productColumns}
                data={productsQuery.data?.items ?? []}
                isLoading={productsQuery.isLoading}
                emptyMessage="No products found. Import or add one."
              />
            </div>

            {/* Pagination */}
            {productsQuery.data && productsQuery.data.totalPages > 1 && (
              <div className="p-3 border-t flex items-center justify-between text-xs text-gray-500">
                <span>{productsQuery.data.total} products total</span>
                <div className="flex gap-1">
                  <button disabled={productPage <= 1} onClick={() => setProductPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">←</button>
                  <span className="px-3 py-1">{productPage} / {productsQuery.data.totalPages}</span>
                  <button disabled={productPage >= productsQuery.data.totalPages} onClick={() => setProductPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">→</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BATCHES TAB ── */}
        {tab === 'batches' && (
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {/* Toolbar */}
            <div className="p-4 border-b flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-2 items-center flex-wrap w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Search batch no, SKU, SAP code..."
                    value={batchSearch}
                    onChange={e => { setBatchSearch(e.target.value); setBatchPage(1); }}
                    className="border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                  {batchSearch && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => { setBatchSearch(''); setBatchPage(1); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >✕</button>
                  )}
                </div>
                <select
                  value={batchClientFilter}
                  onChange={e => { setBatchClientFilter(e.target.value); setBatchProductFilter(undefined); setBatchPage(1); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 max-w-[180px]"
                >
                  <option value="">All Clients</option>
                  {(toolbarClientsQuery.data ?? []).map(c => (
                    <option key={c.id} value={c.id}>{c.companyName}</option>
                  ))}
                </select>
                <div className="w-full sm:w-56">
                  <SearchableSelect
                    options={(allProductsQuery.data ?? []).map(p => ({ value: p.id, label: p.productDescription }))}
                    value={batchProductFilter ?? null}
                    onChange={value => { setBatchProductFilter(value != null ? Number(value) : undefined); setBatchPage(1); }}
                    placeholder="All Products"
                  />
                </div>
                {batchProductFilter && (
                  <button onClick={() => setBatchProductFilter(undefined)} className="text-xs text-gray-400 hover:text-gray-600">✕ Clear</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {canImport && (
                  <>
                    <button onClick={() => downloadTemplate('batches')} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">↓ Template</button>
                    <label className="text-xs px-3 py-1.5 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 flex items-center gap-1 cursor-pointer">
                      {importBatchesMutation.isPending ? 'Importing…' : '↑ Import Excel'}
                      <input ref={batchFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importBatchesMutation.mutate(f); }} />
                    </label>
                  </>
                )}
                {canManage && (
                  <button onClick={openAddBatch} className="text-xs px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">+ Add Batch</button>
                )}
              </div>
            </div>

            {/* Import hint */}
            <div className="px-4 py-2 bg-brand-50 border-b border-brand-100 text-xs text-brand-700">
              <strong>Import format:</strong> A = Product Description&nbsp;·&nbsp;B = Batch No&nbsp;·&nbsp;C = SKU Code&nbsp;·&nbsp;D = Customer SAP Code&nbsp;·&nbsp;E = MFG Date&nbsp;·&nbsp;F = Expiry Date
            </div>

            {/* Table / mobile cards */}
            <div className="px-1 py-3 lg:p-0">
              <ResponsiveTable
                columns={batchColumns}
                data={batchesQuery.data?.items ?? []}
                isLoading={batchesQuery.isLoading}
                emptyMessage="No batches found."
              />
            </div>

            {/* Pagination */}
            {batchesQuery.data && batchesQuery.data.totalPages > 1 && (
              <div className="p-3 border-t flex items-center justify-between text-xs text-gray-500">
                <span>{batchesQuery.data.total} batches total</span>
                <div className="flex gap-1">
                  <button disabled={batchPage <= 1} onClick={() => setBatchPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">←</button>
                  <span className="px-3 py-1">{batchPage} / {batchesQuery.data.totalPages}</span>
                  <button disabled={batchPage >= batchesQuery.data.totalPages} onClick={() => setBatchPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">→</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── UOM TAB ── */}
        {tab === 'uom' && (
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {/* Toolbar */}
            <div className="p-4 border-b flex flex-wrap gap-3 items-center justify-between">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search code, description..."
                  value={uomSearch}
                  onChange={e => { setUomSearch(e.target.value); setUomPage(1); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                {uomSearch && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => { setUomSearch(''); setUomPage(1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >✕</button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {canImport && (
                  <>
                    <button onClick={() => downloadTemplate('uom')} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">↓ Template</button>
                    <label className="text-xs px-3 py-1.5 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 flex items-center gap-1 cursor-pointer">
                      {importUomMutation.isPending ? 'Importing…' : '↑ Import'}
                      <input ref={uomFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                        onChange={e => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          importUomMutation.mutate(f);
                        }} />
                    </label>
                  </>
                )}
                {canExport && (
                  <button
                    onClick={exportUoms}
                    disabled={exportingUoms}
                    className="text-xs px-3 py-1.5 border border-brand-300 rounded-lg text-brand-700 hover:bg-brand-50 flex items-center gap-1 disabled:opacity-50"
                  >
                    {exportingUoms ? 'Exporting…' : '↓ Export'}
                  </button>
                )}
                {canManage && (
                  <button onClick={openAddUom} className="text-xs px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium">+ Add UOM</button>
                )}
              </div>
            </div>

            {/* Import hint */}
            <div className="px-4 py-2 bg-brand-50 border-b border-brand-100 text-xs text-brand-700">
              <strong>Import format:</strong> A = Code&nbsp;·&nbsp;B = Description
            </div>

            {/* Table / mobile cards */}
            <div className="px-1 py-3 lg:p-0">
              <ResponsiveTable
                columns={uomColumns}
                data={uomsQuery.data?.items ?? []}
                isLoading={uomsQuery.isLoading}
                emptyMessage="No UOMs found. Import or add one."
              />
            </div>

            {/* Pagination */}
            {uomsQuery.data && uomsQuery.data.totalPages > 1 && (
              <div className="p-3 border-t flex items-center justify-between text-xs text-gray-500">
                <span>{uomsQuery.data.total} UOMs total</span>
                <div className="flex gap-1">
                  <button disabled={uomPage <= 1} onClick={() => setUomPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">←</button>
                  <span className="px-3 py-1">{uomPage} / {uomsQuery.data.totalPages}</span>
                  <button disabled={uomPage >= uomsQuery.data.totalPages} onClick={() => setUomPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50">→</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── PRODUCT MODAL ── */}
        {productModal.open && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{productModal.editing ? 'Edit Product' : 'Add Product'}</h3>
                <button onClick={() => setProductModal({ open: false, editing: null })} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={e => {
                e.preventDefault();
                if (!productForm.masterPacking || Number(productForm.masterPacking) <= 0) {
                  toast.error('Master Packing must be greater than 0');
                  return;
                }
                if (!productForm.packSize || Number(productForm.packSize) <= 0) {
                  toast.error('Pack Size must be greater than 0');
                  return;
                }
                saveProductMutation.mutate(productForm);
              }} className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Warehouse <span className="text-red-500">*</span></label>
                    <select
                      required
                      value={productForm.warehouseId}
                      onChange={e => setProductForm(f => ({ ...f, warehouseId: e.target.value, clientId: '' }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                    >
                      <option value="">— Select —</option>
                      {(warehousesQuery.data ?? []).map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Client <span className="text-red-500">*</span></label>
                    <select
                      required
                      value={productForm.clientId}
                      onChange={e => setProductForm(f => ({ ...f, clientId: e.target.value }))}
                      disabled={!productForm.warehouseId}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">{productForm.warehouseId ? '— Select —' : 'Select warehouse first'}</option>
                      {(modalClientsQuery.data ?? []).map(c => (
                        <option key={c.id} value={c.id}>{c.companyName}</option>
                      ))}
                    </select>
                    {productForm.warehouseId && !modalClientsQuery.isLoading && (modalClientsQuery.data ?? []).length === 0 && (
                      <p className="mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                        No clients exist for this warehouse
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Product Description <span className="text-red-500">*</span></label>
                    <input required value={productForm.productDescription} onChange={e => setProductForm(f => ({ ...f, productDescription: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. Wheat Flour 50kg" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Item Code</label>
                    <input value={productForm.itemCode} onChange={e => setProductForm(f => ({ ...f, itemCode: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. ITM-001" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Master Packing <span className="text-red-500">*</span></label>
                    <input required type="text" inputMode="numeric" pattern="[0-9]*" value={productForm.masterPacking} onChange={e => setProductForm(f => ({ ...f, masterPacking: INPUT_SANITIZERS.positiveIntegerFormat(e.target.value) }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. 24" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pack Size (units) <span className="text-red-500">*</span></label>
                    <input required type="text" inputMode="numeric" pattern="[0-9]*" value={productForm.packSize} onChange={e => setProductForm(f => ({ ...f, packSize: INPUT_SANITIZERS.positiveIntegerFormat(e.target.value) }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. 12" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Unit Of Measurement <span className="text-red-500">*</span></label>
                    <select required value={productForm.unitType} onChange={e => setProductForm(f => ({ ...f, unitType: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400">
                      <option value="">— Select —</option>
                      {(allUomsQuery.data?.items ?? []).map(u => (
                        <option key={u.id} value={u.code}>{u.code}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Total Qty Per Unit
                      <span className="ml-1 text-gray-400 font-normal">= pack size × master packing</span>
                    </label>
                    <input
                      readOnly
                      value={
                        productForm.packSize && productForm.masterPacking
                          ? (parseFloat(productForm.packSize) * parseFloat(productForm.masterPacking)).toFixed(4)
                          : ''
                      }
                      className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Net Weight (kg)
                      <span className="ml-1 text-gray-400 font-normal">= pack size × master packing</span>
                    </label>
                    <input
                      readOnly
                      value={
                        productForm.packSize && productForm.masterPacking
                          ? (parseFloat(productForm.packSize) * parseFloat(productForm.masterPacking)).toFixed(4)
                          : ''
                      }
                      className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gross Weight (kg) <span className="text-red-500">*</span></label>
                    <input required type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={productForm.grossWeightPerUnit} onChange={e => setProductForm(f => ({ ...f, grossWeightPerUnit: sanitizeDecimalQty(e.target.value) }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. 22" />
                  </div>
                </div>
                {saveProductMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{(saveProductMutation.error as any)?.message ?? 'Failed to save'}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setProductModal({ open: false, editing: null })} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={saveProductMutation.isPending} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                    {saveProductMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── BATCH MODAL ── */}
        {batchModal.open && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{batchModal.editing ? 'Edit Batch' : 'Add Batch'}</h3>
                <button onClick={() => setBatchModal({ open: false, editing: null })} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={e => {
                e.preventDefault();
                if (batchForm.qtyPerPallet && Number(batchForm.qtyPerPallet) <= 0) {
                  toast.error('Qty Per Pallet must be greater than 0');
                  return;
                }
                if (batchForm.mfgDate && batchForm.mfgDate > new Date().toISOString().slice(0, 10)) {
                  toast.error('Manufacturing Date cannot be a future date.');
                  return;
                }
                if (batchForm.mfgDate && batchForm.expiryDate && batchForm.expiryDate < batchForm.mfgDate) {
                  toast.error('Expiry Date cannot be earlier than Manufacturing Date.');
                  return;
                }
                saveBatchMutation.mutate(batchForm);
              }} className="p-4 space-y-3">
                <SearchableSelect
                  label="Product"
                  required
                  options={(allProductsQuery.data ?? []).map(p => ({ value: p.id, label: p.productDescription }))}
                  value={batchForm.productMasterId || null}
                  onChange={value => setBatchForm(f => ({ ...f, productMasterId: value != null ? Number(value) : 0 }))}
                  placeholder="Select product…"
                />
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lot No <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input value={batchForm.lotNo} onChange={e => setBatchForm(f => ({ ...f, lotNo: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. LOT-2024-001" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Batch No <span className="text-red-500">*</span></label>
                  <input required value={batchForm.batchNo} onChange={e => setBatchForm(f => ({ ...f, batchNo: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. BATCH-2024-001" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SKU Code</label>
                    <input value={batchForm.skuCode} onChange={e => setBatchForm(f => ({ ...f, skuCode: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="SKU-001" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Customer SAP Code</label>
                    <input value={batchForm.customerSapCode} onChange={e => setBatchForm(f => ({ ...f, customerSapCode: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="SAP-001" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">MFG Date</label>
                    <input type="date" value={batchForm.mfgDate} onChange={e => setBatchForm(f => ({ ...f, mfgDate: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Expiry Date</label>
                    <input type="date" value={batchForm.expiryDate} onChange={e => setBatchForm(f => ({ ...f, expiryDate: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Qty Per Pallet</label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" value={batchForm.qtyPerPallet} onChange={e => setBatchForm(f => ({ ...f, qtyPerPallet: INPUT_SANITIZERS.positiveIntegerFormat(e.target.value) }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. 100" />
                </div>
                {saveBatchMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{(saveBatchMutation.error as any)?.message ?? 'Failed to save'}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setBatchModal({ open: false, editing: null })} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={saveBatchMutation.isPending} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                    {saveBatchMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── UOM MODAL ── */}
        {uomModal.open && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{uomModal.editing ? 'Edit UOM' : 'Add UOM'}</h3>
                <button onClick={() => setUomModal({ open: false, editing: null })} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <form onSubmit={e => { e.preventDefault(); saveUomMutation.mutate(uomForm); }} className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
                  <input required value={uomForm.code} onChange={e => setUomForm(f => ({ ...f, code: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. BOX" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input value={uomForm.description} onChange={e => setUomForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" placeholder="e.g. Cardboard box" />
                </div>
                {saveUomMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{(saveUomMutation.error as any)?.message ?? 'Failed to save'}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setUomModal({ open: false, editing: null })} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={saveUomMutation.isPending} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
                    {saveUomMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import result modal */}
        {importResult && <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />}

      </div>
      <ConfirmDialog />
    </Layout>
  );
}
